import { createSlice } from "@reduxjs/toolkit";
import { DEFAULT_API_ERROR } from "../../constants/commonConstants";
import {
	iEventType,
	iFilterType,
	iEventBids,
	iEventDetailCMS,
	iFileResponse,
	iEventActionResp,
	IRequest,
} from "../../types/eventTypes";
import {
	getEventsThunk,
	createFilterThunk,
	getEventHistoryThunk,
	respondToEventFilterThunk,
	getEventDetailThunk,
	getEventBidThunk,
	saveBidThunk,
	getEventFilterThunk,
	getEventValuesCMSThunk,
	getCorporateServiceOptionsThunk,
	fileUploadThunk,
	getEventRequestThunk,
	acceptEventRequestThunk,
	declineEventRequestThunk,
} from "./eventThunk";
import { iApiError, iApiSuccess } from "../../types/commonTypes";
import { ICorporateServiceOption } from "../../interfaces/corporateEvent";
import { isObjectEmpty } from "../../helpers/common";

export interface iEventStoreState {
	eventList: Array<iEventType> | [];
	isFilterCreate: boolean;
	isBidSave: boolean;
	isLoading: boolean;
	isLoadingEventList: boolean;
	latestListRequestId: string | null;
	apiError: iApiError | null;
	event: iEventType;
	bids: iEventBids;
	apiSuccess: iApiSuccess | null;
	filters: iFilterType | null;
	cms: iEventDetailCMS;
	corporateServiceOptions: ICorporateServiceOption[];
	corporateServiceOptionsStatus: "idle" | "loading" | "loaded" | "failed";
	fileResp: iFileResponse;
	eventActionResp: iEventActionResp;
	eventRequests: IRequest[];
	showThankYouModal: boolean;
	bidsLoading: boolean;
	bidsApiError: iApiError | null;
}

const initialState: iEventStoreState = {
	eventList: [] as Array<iEventType>,
	isLoading: false,
	isLoadingEventList: false,
	latestListRequestId: null,
	apiError: null,
	isFilterCreate: false,
	event: {} as iEventType,
	bids: {} as iEventBids,
	isBidSave: false,
	apiSuccess: null,
	filters: {} as iFilterType,
	cms: {} as iEventDetailCMS,
	corporateServiceOptions: [],
	corporateServiceOptionsStatus: "idle",
	fileResp: {} as iFileResponse,
	eventActionResp: {} as iEventActionResp,
	eventRequests: [],
	showThankYouModal: false,
	bidsLoading: false,
	bidsApiError: null,
};

const eventSlice = createSlice({
	name: "events",
	initialState,
	reducers: {
		setShowThankYouModal(state, action) {
			state.showThankYouModal = action.payload;
		},
		resetApiErrorEvent(state) {
			state.apiError = null;
			state.apiSuccess = null;
			state.filters = null;
		},
		resetEventDetail(state) {
			state.event = {} as iEventType;
			state.bids = {} as iEventBids;
		},
	},
	extraReducers(builder) {
		builder.addCase(getEventsThunk.fulfilled, (state, action) => {
			state.isLoading = false;
			if (action.meta.requestId !== state.latestListRequestId) return;
			state.eventList = action.payload;
			state.apiError = null;
			state.isLoadingEventList = false;
		});
		builder.addCase(getEventsThunk.pending, (state, action: any) => {
			state.apiError = null;
			state.isLoading = action.meta.arg.isLoading;
			state.latestListRequestId = action.meta.requestId;
			if (action.meta.arg.isLoading) {
				state.isLoadingEventList = true;
				state.eventList = [];
			}
		});
		builder.addCase(getEventsThunk.rejected, (state, action) => {
			state.isLoading = false;
			if (action.meta.requestId !== state.latestListRequestId) return;
			state.apiError = action.payload ?? DEFAULT_API_ERROR;
			state.eventList = [];
			state.isLoadingEventList = false;
		});

		builder.addCase(createFilterThunk.fulfilled, (state) => {
			state.isLoading = false;
			state.isFilterCreate = true;
		});
		builder.addCase(createFilterThunk.pending, (state) => {
			state.apiError = null;
			state.isLoading = true;
			state.apiSuccess = null;
			state.isFilterCreate = false;
		});
		builder.addCase(createFilterThunk.rejected, (state) => {
			state.isLoading = false;
		});
		builder.addCase(getEventHistoryThunk.fulfilled, (state, action) => {
			state.isLoading = false;
			if (action.meta.requestId !== state.latestListRequestId) return;
			state.eventList = action.payload;
			state.apiError = null;
			state.isLoadingEventList = false;
		});
		builder.addCase(getEventHistoryThunk.pending, (state, action: any) => {
			state.apiError = null;
			state.isLoading = action.meta.arg.isLoading;
			state.latestListRequestId = action.meta.requestId;
			if (action.meta.arg.isLoading) {
				state.isLoadingEventList = true;
				state.eventList = [];
			}
		});
		builder.addCase(getEventHistoryThunk.rejected, (state, action) => {
			state.isLoading = false;
			if (action.meta.requestId !== state.latestListRequestId) return;
			state.apiError = action.payload ?? DEFAULT_API_ERROR;
			state.eventList = [];
			state.isLoadingEventList = false;
		});
		builder.addCase(respondToEventFilterThunk.fulfilled, (state, action) => {
			state.isLoading = false;
			state.eventActionResp = action.payload;
			state.event = state.eventActionResp?.event;
			state.apiError = null;
			state.apiSuccess = {
				statusCode: 200,
				message: state.eventActionResp?.message,
			};
		});
		builder.addCase(respondToEventFilterThunk.pending, (state) => {
			state.apiError = null;
			state.isLoading = true;
		});
		builder.addCase(respondToEventFilterThunk.rejected, (state, action) => {
			state.apiError = action.payload ?? DEFAULT_API_ERROR;
			state.isLoading = false;
		});
		builder.addCase(getEventDetailThunk.fulfilled, (state, action) => {
			state.isLoading = false;
			state.event = action.payload;
			state.apiError = null;
		});
		builder.addCase(getEventDetailThunk.pending, (state) => {
			state.apiError = null;
			state.isLoading = isObjectEmpty(state.event) ? true : false;
		});
		builder.addCase(getEventDetailThunk.rejected, (state, action) => {
			state.apiError = action.payload ?? DEFAULT_API_ERROR;
			state.isLoading = false;
		});

		builder.addCase(getEventBidThunk.fulfilled, (state, action) => {
			state.bidsLoading = false;
			state.bids = action.payload;
			state.apiError = null;
		});
		builder.addCase(getEventBidThunk.pending, (state) => {
			state.apiError = null;
			state.bidsLoading = isObjectEmpty(state.bids) ? true : false;
		});
		builder.addCase(getEventBidThunk.rejected, (state, action) => {
			state.apiError = action.payload ?? DEFAULT_API_ERROR;
			state.bidsLoading = false;
		});

		builder.addCase(saveBidThunk.fulfilled, (state, action) => {
			state.isLoading = false;
			state.isBidSave = true;
			state.apiSuccess = action.payload;
		});
		builder.addCase(saveBidThunk.pending, (state) => {
			state.apiError = null;
			state.isLoading = true;
			state.isBidSave = false;
		});
		builder.addCase(saveBidThunk.rejected, (state, action) => {
			state.apiError = action.payload ?? DEFAULT_API_ERROR;
			state.isLoading = false;
		});

		builder.addCase(getEventFilterThunk.fulfilled, (state, action) => {
			state.filters = action.payload;
			state.apiError = null;
			state.isLoading = false;
		});
		builder.addCase(getEventFilterThunk.pending, (state) => {
			state.apiError = null;
			state.isLoading = true;
			state.filters = null;
		});
		builder.addCase(getEventFilterThunk.rejected, (state, action) => {
			state.apiError = action.payload ?? DEFAULT_API_ERROR;
			state.isLoading = false;
		});
		builder.addCase(getCorporateServiceOptionsThunk.pending, (state) => {
			state.corporateServiceOptionsStatus = "loading";
		});
		builder.addCase(getCorporateServiceOptionsThunk.fulfilled, (state, action) => {
			state.corporateServiceOptions = action.payload;
			state.corporateServiceOptionsStatus = "loaded";
		});
		builder.addCase(getCorporateServiceOptionsThunk.rejected, (state) => {
			state.corporateServiceOptionsStatus = "failed";
		});
		builder.addCase(getEventValuesCMSThunk.fulfilled, (state, action) => {
			state.cms = action.payload;
			state.apiError = null;
			state.isLoading = false;
		});
		builder.addCase(getEventValuesCMSThunk.pending, (state) => {
			state.apiError = null;
			state.isLoading = true;
		});
		builder.addCase(getEventValuesCMSThunk.rejected, (state, action) => {
			state.apiError = action.payload ?? DEFAULT_API_ERROR;
			state.isLoading = false;
		});

		builder.addCase(fileUploadThunk.fulfilled, (state, action) => {
			state.fileResp = action.payload;
			state.apiError = null;
			state.isLoading = false;
		});
		builder.addCase(fileUploadThunk.pending, (state) => {
			state.apiError = null;
			state.isLoading = true;
		});
		builder.addCase(fileUploadThunk.rejected, (state, action) => {
			state.apiError = action.payload ?? DEFAULT_API_ERROR;
			state.isLoading = false;
		});

		builder.addCase(getEventRequestThunk.fulfilled, (state, action) => {
			state.eventRequests = action.payload;
		});

		builder.addCase(getEventRequestThunk.rejected, (state) => {
			state.eventRequests = [];
		});

		builder.addCase(acceptEventRequestThunk.fulfilled, (state, action) => {
			state.apiSuccess = action.payload as iApiSuccess;
		});

		builder.addCase(acceptEventRequestThunk.rejected, (state, action) => {
			state.apiError = action.payload as iApiError;
		});

		builder.addCase(declineEventRequestThunk.fulfilled, (state, action) => {
			state.apiSuccess = action.payload as iApiSuccess;
		});

		builder.addCase(declineEventRequestThunk.rejected, (state, action) => {
			state.apiError = action.payload as iApiError;
		});
	},
});

export const { setShowThankYouModal, resetApiErrorEvent, resetEventDetail } = eventSlice.actions;
export default eventSlice.reducer;
